import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  paperIdSelector,
  rewritePanelSentencesSelector,
} from '@chegg-wt/core-wa/state/selectors'
import { AppState } from '@chegg-wt/core/state'
import MediaQuery from 'react-responsive'
import { breakpoints } from '@chegg-ui/theme'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import { setEnabledWAFeatures as setEnabledWAFeaturesAction } from '@chegg-wt/core-wa/state/actions'
import { SentenceOffset, WAFeatures } from '@chegg-wt/core-wa/state/types'
import { WRITING_ASSISTANT_FEATURES } from '@chegg-wt/core/state/optimizely/constants'
import useSprigTriggerExitIntent, {
  SprigExitIntentKey,
} from '@chegg-wt/core-wa/hooks/useSprigTriggerExitIntent'
import { setSessionStorageItem } from '@chegg-wt/core/utils/sessionStorage'
import CardsContainer from '../CardsContainer/CardsContainer'
import EditorContainer from '../EditorContainer/EditorContainer'
import ProofReaderLeftPanel from '../ProofReaderLeftPanel/ProofReaderLeftPanel'
import { MENU_ITEM } from '../ProofReaderLeftPanel/type'
import { HeaderContainer } from '../HeaderContainer/HeaderContainer'
import QARightPanel from '../QuickActionRightPanel'
import { ToastPortal } from '../ToastNotification/ToastPortal'
import SmallScreenModalBlocker from '../SmallScreenModalBlocker'

import { AiPoweredDisclaimer } from '../EditorFooter/AiPoweredDisclaimer'
import {
  ProofReaderContentContainer,
  ProofReaderLayoutContainer,
  WorkspaceContainer,
} from './styled'

interface ProofReaderLayoutProps {
  rewritePanelSentences: SentenceOffset[]
  setEnabledWAFeatures: typeof setEnabledWAFeaturesAction
  paperId: string
}

const ProofReaderLayout = ({
  rewritePanelSentences,
  setEnabledWAFeatures,
  paperId,
}: ProofReaderLayoutProps) => {
  const { isEnabled, variables } = useSafeOptimizelyFeature(WRITING_ASSISTANT_FEATURES)
  const [isUndoRedoEnabled, setUndoRedoEnabled] = useState(null)

  const { initiateSurveyOnPageExitIntent } = useSprigTriggerExitIntent()

  useEffect(() => {
    initiateSurveyOnPageExitIntent(
      SprigExitIntentKey.EbWritingAssistantSurveyKey,
      paperId
    )
  }, [paperId, initiateSurveyOnPageExitIntent])

  useEffect(() => {
    setSessionStorageItem('isMultiPaperEnabled', variables?.multiPaper ? 'true' : 'false')

    if (isEnabled) {
      setEnabledWAFeatures(variables as WAFeatures)
      setUndoRedoEnabled(!!variables?.redoUndo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled])

  return (
    <>
      <ProofReaderLayoutContainer>
        <HeaderContainer />
        <MediaQuery maxWidth={breakpoints.md.max}>
          <SmallScreenModalBlocker />
        </MediaQuery>
        <MediaQuery minWidth={breakpoints.lg.min}>
          <ProofReaderContentContainer>
            <ProofReaderLeftPanel activeMenuItem={MENU_ITEM.WRITING} />
            <WorkspaceContainer>
              <EditorContainer isRewritePanelActive={rewritePanelSentences?.length > 0} />
              {rewritePanelSentences?.length > 0 ? <QARightPanel /> : <CardsContainer />}
            </WorkspaceContainer>
          </ProofReaderContentContainer>
          {isUndoRedoEnabled && <AiPoweredDisclaimer className="undoRedo" />}
        </MediaQuery>
      </ProofReaderLayoutContainer>
      <ToastPortal />
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  rewritePanelSentences: rewritePanelSentencesSelector(state),
  paperId: paperIdSelector(state),
})

const mapDispatchToProps = {
  setEnabledWAFeatures: setEnabledWAFeaturesAction,
}

export default connect(mapStateToProps, mapDispatchToProps)(ProofReaderLayout)
